import type { PersonalScheduleProgram } from '@/domain'
import { mergePersonalSchedulePrograms } from '@/lib/programmeLifecycle'

export const PROGRAMME_PROGRESS_KIND = 'p.ps.progress'

export type PersonalScheduleDatalakeItem = {
    id?: string
    kind?: string
    corellationId?: string
    data: string
}

function normalizeLegacyPersonalSchedule(value: unknown): unknown {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return value
    const candidate = value as Record<string, unknown>
    const history = candidate.history === undefined ? [] : candidate.history
    let endDate = candidate.endDate
    if (endDate === undefined && Array.isArray(candidate.schedule)) {
        endDate = candidate.schedule.reduce<number>((latest, entry) => {
            if (!entry || typeof entry !== 'object') return latest
            const date = (entry as Record<string, unknown>).date
            return typeof date === 'number' && Number.isFinite(date)
                ? Math.max(latest, date)
                : latest
        }, 0)
    }

    return {
        ...candidate,
        endDate,
        history
    }
}

function isPersonalScheduleProgram(
    value: unknown,
    expectedProgramId?: string
): value is PersonalScheduleProgram {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return false
    const candidate = value as Partial<PersonalScheduleProgram>
    const validOptionalNumber = (number: unknown) =>
        number === undefined ||
        (typeof number === 'number' && Number.isFinite(number))
    const validOptionalCounter = (number: unknown) =>
        number === undefined ||
        (typeof number === 'number' &&
            Number.isSafeInteger(number) &&
            number >= 0 &&
            number <= 2_147_483_647)
    const validSchedule =
        Array.isArray(candidate.schedule) &&
        candidate.schedule.every(
            (entry) =>
                Boolean(entry) &&
                typeof entry.id === 'string' &&
                Number.isFinite(entry.date) &&
                typeof entry.lessonId === 'string' &&
                (entry.sourceScheduleId === undefined ||
                    typeof entry.sourceScheduleId === 'string')
        )
    const validHistory =
        candidate.history === undefined ||
        (Array.isArray(candidate.history) &&
            candidate.history.every(
                (entry) =>
                    Boolean(entry) &&
                    typeof entry.id === 'string' &&
                    typeof entry.kind === 'string' &&
                    typeof entry.referenceId === 'string' &&
                    Number.isFinite(entry.startedAt) &&
                    validOptionalNumber(entry.cancelledAt) &&
                    validOptionalNumber(entry.finishedAt) &&
                    Array.isArray(entry.setsFinishTime) &&
                    entry.setsFinishTime.every((time) => Number.isFinite(time))
            ))
    const validStatus = [
        'draft',
        'started',
        'finished',
        'stopped',
        'archived'
    ].includes(candidate.status || '')
    return (
        typeof candidate.id === 'string' &&
        candidate.id.length > 0 &&
        typeof candidate.programId === 'string' &&
        (!expectedProgramId || candidate.programId === expectedProgramId) &&
        typeof candidate.originalProgramId === 'string' &&
        validStatus &&
        typeof candidate.startDate === 'string' &&
        typeof candidate.starTime === 'string' &&
        Number.isFinite(candidate.endDate) &&
        validOptionalNumber(candidate.timestampUTC) &&
        validOptionalCounter(candidate.sourceContentVersion) &&
        validOptionalCounter(candidate.scheduleRevision) &&
        validOptionalCounter(candidate.enrollmentSequence) &&
        validHistory &&
        validSchedule
    )
}

export function mergePersonalScheduleItems(
    items: PersonalScheduleDatalakeItem[],
    options?: { strictCanonical?: boolean }
): PersonalScheduleProgram | undefined {
    const parsedSnapshots = items
        .filter(
            (item) =>
                item.kind === 'p.ps' || item.kind === PROGRAMME_PROGRESS_KIND
        )
        .map((item) => {
            try {
                const parsed: unknown = JSON.parse(item.data)
                const snapshot =
                    item.kind === 'p.ps'
                        ? normalizeLegacyPersonalSchedule(parsed)
                        : parsed
                if (!isPersonalScheduleProgram(snapshot, item.corellationId)) {
                    throw new Error('Invalid personal schedule payload')
                }
                return {
                    kind: item.kind,
                    snapshot
                }
            } catch (error) {
                if (item.kind === 'p.ps' && options?.strictCanonical)
                    throw error
                console.error(
                    'Ignoring invalid programme progress snapshot:',
                    error
                )
                return undefined
            }
        })
        .filter(
            (
                parsed
            ): parsed is {
                kind: string | undefined
                snapshot: PersonalScheduleProgram
            } => parsed !== undefined
        )
    const canonical = [...parsedSnapshots]
        .reverse()
        .find((parsed) => parsed.kind === 'p.ps')?.snapshot
    const merged = [...parsedSnapshots]
        // Writer snapshots contribute history/status/version, while the canonical
        // document remains authoritative for unrelated editable fields.
        .sort((a, b) => Number(a.kind === 'p.ps') - Number(b.kind === 'p.ps'))
        .reduce<PersonalScheduleProgram | undefined>(
            (current, parsed) =>
                current
                    ? mergePersonalSchedulePrograms(current, parsed.snapshot)
                    : parsed.snapshot,
            undefined
        )
    if (!merged || !canonical || canonical.id !== merged.id) return merged
    return {
        ...merged,
        startDate: canonical.startDate,
        starTime: canonical.starTime,
        timestampUTC: canonical.timestampUTC
    }
}

export function mergePersonalScheduleItemGroups(
    items: PersonalScheduleDatalakeItem[]
): PersonalScheduleProgram[] {
    const groups = new Map<string, PersonalScheduleDatalakeItem[]>()
    for (const item of items) {
        if (!item.corellationId) continue
        const group = groups.get(item.corellationId) || []
        group.push(item)
        groups.set(item.corellationId, group)
    }
    return Array.from(groups.values())
        .map((group) => mergePersonalScheduleItems(group))
        .filter((pps): pps is PersonalScheduleProgram => pps !== undefined)
}
